import React, { useMemo, useState } from 'react';

/** Compact money/size scale — Yahoo reports market cap and revenue in raw units. */
function formatCompact(v) {
    const n = Number(v);
    if (!Number.isFinite(n)) return null;
    const abs = Math.abs(n);
    const scale = [
        [1e12, 'T'],
        [1e9, 'B'],
        [1e6, 'M'],
        [1e3, 'K'],
    ].find(([size]) => abs >= size);
    if (!scale) return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
    const [size, suffix] = scale;
    return `${(n / size).toFixed(2)}${suffix}`;
}

function makeMoneyFormatter(currency) {
    const code =
        currency && String(currency).trim().length === 3
            ? String(currency).trim().toUpperCase()
            : null;
    return (v, digits = 2) => {
        const n = Number(v);
        if (!Number.isFinite(n)) return null;
        if (code) {
            try {
                return new Intl.NumberFormat(undefined, {
                    style: 'currency',
                    currency: code,
                    minimumFractionDigits: digits,
                    maximumFractionDigits: digits,
                }).format(n);
            } catch {
                /* fall through to a plain number */
            }
        }
        return n.toLocaleString(undefined, {
            minimumFractionDigits: digits,
            maximumFractionDigits: digits,
        });
    };
}

function formatPercent(v, digits = 2) {
    const n = Number(v);
    if (!Number.isFinite(n)) return null;
    return `${n >= 0 ? '' : '−'}${Math.abs(n).toFixed(digits)}%`;
}

function formatRatio(v, digits = 2) {
    const n = Number(v);
    if (!Number.isFinite(n)) return null;
    return n.toFixed(digits);
}

function formatCount(v) {
    const n = Number(v);
    if (!Number.isFinite(n)) return null;
    return Math.round(n).toLocaleString();
}

function formatDay(ymd) {
    if (!ymd) return null;
    const [y, m, d] = String(ymd).split('-').map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
}

function formatText(v) {
    const s = String(v ?? '').trim();
    return s || null;
}

/** "strong_buy" → "Strong buy" */
function formatRecommendation(v) {
    const s = formatText(v);
    if (!s) return null;
    const words = s.replace(/_/g, ' ');
    return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Key statistics for one symbol, straight from Yahoo via the quotes service.
 * Tiles with no value are dropped, and a group with no tiles is not rendered —
 * coverage varies a lot between exchanges and between equities and funds.
 */
export default function StockStatsCard({ symbol, stats, loading, error, onRetry, currency }) {
    const [open, setOpen] = useState(true);

    const money = useMemo(
        () => makeMoneyFormatter(stats?.currency || currency),
        [stats?.currency, currency],
    );

    const groups = useMemo(() => {
        if (!stats) return [];

        const range = (lo, hi) => {
            const a = money(lo);
            const b = money(hi);
            if (!a || !b) return a || b || null;
            return `${a} – ${b}`;
        };

        const defs = [
            {
                title: 'Market',
                tiles: [
                    ['Price', money(stats.price)],
                    ['Change', formatPercent(stats.change_percent)],
                    ['Previous close', money(stats.previous_close)],
                    ['Open', money(stats.open)],
                    ["Day's range", range(stats.day_low, stats.day_high)],
                    ['52-week range', range(stats.fifty_two_week_low, stats.fifty_two_week_high)],
                    ['52-week change', formatPercent(stats.fifty_two_week_change_percent)],
                    ['50-day average', money(stats.fifty_day_average)],
                    ['200-day average', money(stats.two_hundred_day_average)],
                    ['Volume', formatCount(stats.volume)],
                    ['Average volume', formatCount(stats.average_volume)],
                    ['Average volume (10d)', formatCount(stats.average_volume_10d)],
                    ['Beta', formatRatio(stats.beta)],
                ],
            },
            {
                title: 'Valuation',
                tiles: [
                    ['Market cap', formatCompact(stats.market_cap)],
                    ['Enterprise value', formatCompact(stats.enterprise_value)],
                    ['Trailing P/E', formatRatio(stats.trailing_pe)],
                    ['Forward P/E', formatRatio(stats.forward_pe)],
                    ['PEG ratio', formatRatio(stats.peg_ratio)],
                    ['Price / book', formatRatio(stats.price_to_book)],
                    ['Price / sales', formatRatio(stats.price_to_sales)],
                    ['Book value', money(stats.book_value)],
                    ['EPS (trailing)', money(stats.trailing_eps)],
                    ['EPS (forward)', money(stats.forward_eps)],
                    ['Shares outstanding', formatCompact(stats.shares_outstanding)],
                    ['Float', formatCompact(stats.float_shares)],
                ],
            },
            {
                title: 'Dividends',
                tiles: [
                    ['Annual rate', money(stats.dividend_rate)],
                    ['Yield', formatPercent(stats.dividend_yield_percent)],
                    ['5-year average yield', formatPercent(stats.five_year_avg_dividend_yield_percent)],
                    ['Payout ratio', formatPercent(stats.payout_ratio_percent)],
                    ['Last payment', money(stats.last_dividend_value)],
                    ['Last payment date', formatDay(stats.last_dividend_date)],
                    ['Ex-dividend date', formatDay(stats.ex_dividend_date)],
                ],
            },
            {
                title: 'Financials',
                tiles: [
                    ['Revenue', formatCompact(stats.total_revenue)],
                    ['EBITDA', formatCompact(stats.ebitda)],
                    ['Net income', formatCompact(stats.net_income)],
                    ['Free cash flow', formatCompact(stats.free_cashflow)],
                    ['Total cash', formatCompact(stats.total_cash)],
                    ['Total debt', formatCompact(stats.total_debt)],
                    // Yahoo reports debt/equity as a percentage, not a multiple.
                    ['Debt / equity', formatPercent(stats.debt_to_equity)],
                    ['Current ratio', formatRatio(stats.current_ratio)],
                    ['Profit margin', formatPercent(stats.profit_margin_percent)],
                    ['Operating margin', formatPercent(stats.operating_margin_percent)],
                    ['Return on equity', formatPercent(stats.return_on_equity_percent)],
                    ['Revenue growth', formatPercent(stats.revenue_growth_percent)],
                    ['Earnings growth', formatPercent(stats.earnings_growth_percent)],
                ],
            },
            {
                title: 'Analysts',
                tiles: [
                    ['Recommendation', formatRecommendation(stats.recommendation)],
                    ['Mean target', money(stats.target_mean_price)],
                    ['Target range', range(stats.target_low_price, stats.target_high_price)],
                    ['Analyst opinions', formatCount(stats.analyst_opinions)],
                    ['Next earnings', formatDay(stats.earnings_date)],
                ],
            },
            {
                title: 'Profile',
                tiles: [
                    ['Sector', formatText(stats.sector)],
                    ['Industry', formatText(stats.industry)],
                    ['Country', formatText(stats.country)],
                    ['Employees', formatCount(stats.employees)],
                    ['Exchange', formatText(stats.exchange)],
                    ['Type', formatText(stats.quote_type)],
                ],
            },
        ];

        return defs
            .map(g => ({ ...g, tiles: g.tiles.filter(([, value]) => value != null && value !== '') }))
            .filter(g => g.tiles.length > 0);
    }, [stats, money]);

    const asOf = stats?.quote_time
        ? new Date(stats.quote_time).toLocaleString(undefined, {
              dateStyle: 'medium',
              timeStyle: 'short',
          })
        : null;

    // While the market is trading the page re-reads these stats every minute,
    // so the quote time is what shows the number is moving.
    const live = Boolean(stats?.market_open);

    return (
        <section className="stock-stats-card" aria-labelledby="stock-stats-heading">
            <div className="stock-stats-header">
                <h2 className="stock-stats-heading" id="stock-stats-heading">
                    Key statistics
                    {stats?.market_state && (
                        <span
                            className={`stock-stats-market-state${stats.market_open ? ' is-open' : ''}`}
                        >
                            {stats.market_open ? 'Market open' : stats.market_state.toLowerCase()}
                        </span>
                    )}
                </h2>
                <div className="stock-stats-header-actions">
                    {asOf && (
                        <span className={`stock-stats-asof${live ? ' is-live' : ''}`}>
                            {live && <span className="stock-stats-live-dot" aria-hidden="true" />}
                            Quote {asOf}
                        </span>
                    )}
                    {groups.length > 0 && (
                        <button
                            type="button"
                            className="quote-btn quote-btn-secondary stock-stats-toggle"
                            onClick={() => setOpen(o => !o)}
                            aria-expanded={open}
                        >
                            {open ? 'Hide' : 'Show'}
                        </button>
                    )}
                </div>
            </div>

            {loading && <p className="quote-toolbar-hint stock-stats-note">Loading statistics…</p>}

            {!loading && error && (
                <p className="quote-toolbar-hint stock-stats-note">
                    Statistics for {symbol} could not be loaded: {error}{' '}
                    {onRetry && (
                        <button type="button" className="stock-stats-retry" onClick={onRetry}>
                            Try again
                        </button>
                    )}
                </p>
            )}

            {!loading && !error && groups.length === 0 && (
                <p className="quote-toolbar-hint stock-stats-note">
                    Yahoo Finance reports no statistics for {symbol}.
                </p>
            )}

            {!loading && !error && open && groups.length > 0 && (
                <>
                    {groups.map(group => (
                        <div className="stock-stats-group" key={group.title}>
                            <h3 className="stock-stats-group-title">{group.title}</h3>
                            <dl className="stock-stats-grid">
                                {group.tiles.map(([label, value]) => (
                                    <div className="stock-stats-tile" key={label}>
                                        <dt className="stock-stats-label">{label}</dt>
                                        <dd className="stock-stats-value">{value}</dd>
                                    </div>
                                ))}
                            </dl>
                        </div>
                    ))}

                    {formatText(stats.website) && (
                        <p className="stock-stats-note">
                            <a
                                href={stats.website}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="stock-stats-link"
                            >
                                Company website
                            </a>
                        </p>
                    )}

                    {formatText(stats.summary) && (
                        <details className="stock-stats-summary">
                            <summary>Business summary</summary>
                            <p>{stats.summary}</p>
                        </details>
                    )}
                </>
            )}
        </section>
    );
}
